import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send, MailCheck, ArrowLeft, MessageSquare } from "lucide-react";
import TechBackground from "@/components/TechBackground";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /contact?subject=<key>&email=<prefill>&message=<prefill>
 *
 * Single-page support form. Replaces "reply to this email" prompts in
 * transactional emails (iter 39+) so every help request lands in the
 * orders inbox as a structured ticket instead of getting lost in a thread.
 *
 * URL params (all optional):
 *   - subject  — preselect the dropdown (`login-support`, `billing-question`, …)
 *   - email    — prefill the email field (e.g. from a logged-in profile)
 *   - message  — prefill the message body (e.g. error context)
 */
export default function Contact() {
    const [sp] = useSearchParams();
    // Seed the initial options with the URL-provided subject so the <select>
    // has a matching option before GET /contact/subjects resolves (avoids a
    // brief fallback to "General enquiry" on deep-links like ?subject=demo-request-lab).
    const urlSubject = sp.get("subject");
    const [subjects, setSubjects] = useState(() => {
        const base = [{ key: "general", label: "General enquiry" }];
        return urlSubject && urlSubject !== "general" ? [{ key: urlSubject, label: "Loading…" }, ...base] : base;
    });
    const [form, setForm] = useState({
        name: "",
        email: sp.get("email") || "",
        subject: sp.get("subject") || "general",
        message: sp.get("message") || "",
    });
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);

    // Pull the allowed subject keys from the backend so the dropdown stays
    // in sync with `SUBJECT_LABELS` in routes/contact.py without manual copy.
    useEffect(() => {
        let alive = true;
        axios.get(`${API}/contact/subjects`)
            .then(({ data }) => { if (alive && Array.isArray(data?.subjects)) setSubjects(data.subjects); })
            .catch(() => { /* fall back to default 'general' */ });
        return () => { alive = false; };
    }, []);

    const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.email || form.message.trim().length < 10) {
            toast.error("Please include your email and a short message (10+ chars).");
            return;
        }
        setBusy(true);
        try {
            await axios.post(`${API}/contact`, form);
            setSent(true);
            toast.success("Got it — we'll get back to you shortly.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't send right now. Try again in a minute.");
        } finally {
            setBusy(false);
        }
    };

    // Human-readable label for the currently-selected subject (used in success card).
    const currentLabel = subjects.find((s) => s.key === form.subject)?.label || "General enquiry";

    return (
        <div data-testid="contact-page" className="min-h-[calc(100vh-68px)] relative overflow-hidden bg-[#0B1120] text-white">
            <TechBackground variant="dark" density={0.9} />
            <div className="absolute -top-32 -right-32 w-[520px] h-[520px] tr-glow bg-cyan-500/25" />
            <div className="absolute bottom-0 -left-32 w-[420px] h-[420px] tr-glow bg-teal-500/20" />

            <div className="relative max-w-3xl mx-auto px-6 sm:px-10 py-16 lg:py-20">
                <Link to="/" className="inline-flex items-center gap-1.5 text-[12.5px] text-white/60 hover:text-white/90 mb-8 font-mono-tr tracking-wide" data-testid="contact-back-home">
                    <ArrowLeft size={13} /> Back to home
                </Link>

                <div className="tr-overline text-cyan-400 mb-3">· Toothrocket · Support</div>
                <h1 className="font-display font-light text-4xl sm:text-5xl tracking-tight leading-[1.05] text-white">
                    Need a hand?<br />
                    <span className="italic text-cyan-300/90 font-extralight">Drop us a line.</span>
                </h1>
                <p className="mt-5 text-white/65 text-[15px] leading-relaxed max-w-xl">
                    Every ticket lands directly in our orders inbox. We aim to reply within 4 business hours, and within 1 hour for login or production-down issues.
                </p>

                {/* Form */}
                {!sent ? (
                    <form
                        onSubmit={submit}
                        className="mt-10 rounded-2xl bg-white/[0.04] backdrop-blur border border-white/10 p-7 space-y-5"
                        data-testid="contact-form"
                    >
                        <div className="grid sm:grid-cols-2 gap-4">
                            <label className="block">
                                <div className="font-mono-tr text-[10.5px] tracking-[0.22em] uppercase text-white/55 mb-1.5">Your name <span className="opacity-50">(optional)</span></div>
                                <input
                                    value={form.name}
                                    onChange={(e) => set("name", e.target.value)}
                                    data-testid="contact-name"
                                    placeholder="Dr Anita Rao"
                                    className="w-full px-3.5 py-2.5 rounded-lg bg-white/[0.05] border border-white/10 focus:border-cyan-400/60 focus:ring-2 focus:ring-cyan-400/15 outline-none text-[14px] text-white placeholder-white/30"
                                />
                            </label>
                            <label className="block">
                                <div className="font-mono-tr text-[10.5px] tracking-[0.22em] uppercase text-white/55 mb-1.5">Your email</div>
                                <input
                                    type="email"
                                    required
                                    value={form.email}
                                    onChange={(e) => set("email", e.target.value)}
                                    data-testid="contact-email"
                                    placeholder="you@clinic.com"
                                    className="w-full px-3.5 py-2.5 rounded-lg bg-white/[0.05] border border-white/10 focus:border-cyan-400/60 focus:ring-2 focus:ring-cyan-400/15 outline-none text-[14px] text-white placeholder-white/30"
                                />
                            </label>
                        </div>

                        <label className="block">
                            <div className="font-mono-tr text-[10.5px] tracking-[0.22em] uppercase text-white/55 mb-1.5">What can we help with?</div>
                            <select
                                value={form.subject}
                                onChange={(e) => set("subject", e.target.value)}
                                data-testid="contact-subject"
                                className="w-full px-3.5 py-2.5 rounded-lg bg-white/[0.05] border border-white/10 focus:border-cyan-400/60 focus:ring-2 focus:ring-cyan-400/15 outline-none text-[14px] text-white"
                            >
                                {subjects.map((s) => (
                                    <option key={s.key} value={s.key} className="bg-slate-900">{s.label}</option>
                                ))}
                            </select>
                        </label>

                        <label className="block">
                            <div className="font-mono-tr text-[10.5px] tracking-[0.22em] uppercase text-white/55 mb-1.5">Your message</div>
                            <textarea
                                required
                                rows={6}
                                value={form.message}
                                onChange={(e) => set("message", e.target.value)}
                                data-testid="contact-message"
                                placeholder="Tell us what's going on — error message, what you tried, browser, etc."
                                className="w-full px-3.5 py-2.5 rounded-lg bg-white/[0.05] border border-white/10 focus:border-cyan-400/60 focus:ring-2 focus:ring-cyan-400/15 outline-none text-[14px] text-white placeholder-white/30 leading-relaxed resize-y"
                            />
                            <div className="mt-1.5 text-[11.5px] text-white/40 font-mono-tr">
                                {form.message.length} / 4000 characters · please include details that help us reproduce the issue
                            </div>
                        </label>

                        <button
                            type="submit"
                            disabled={busy}
                            data-testid="contact-submit"
                            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white font-medium text-[14px] transition-colors disabled:opacity-50"
                        >
                            {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                            Send to support
                        </button>
                    </form>
                ) : (
                    <div
                        className="mt-10 rounded-2xl bg-emerald-500/10 backdrop-blur border border-emerald-400/30 p-7"
                        data-testid="contact-success"
                    >
                        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-500/20 text-emerald-300">
                            <MailCheck size={22} />
                        </div>
                        <h2 className="mt-4 font-display text-2xl tracking-tight text-white">Ticket received.</h2>
                        <p className="mt-2 text-[14px] text-white/70 leading-relaxed">
                            We logged your <strong>{currentLabel}</strong> request from <strong>{form.email}</strong>. Look for our reply within 4 business hours.
                        </p>
                        <button
                            onClick={() => { setSent(false); setForm({ name: "", email: "", subject: "general", message: "" }); }}
                            data-testid="contact-new-ticket"
                            className="mt-5 inline-flex items-center gap-1.5 text-[13px] text-cyan-300 hover:text-cyan-200"
                        >
                            <MessageSquare size={13} /> Send another ticket
                        </button>
                    </div>
                )}

                <div className="mt-10 text-[12px] text-white/35 font-mono-tr tracking-wide">
                    Replies to automated emails (e.g. magic-link, welcome, invoices) are not monitored — please always use this form.
                </div>
            </div>
        </div>
    );
}
